// app/javascript/extensions/zettel_extractor_extension.js
import { EditorView, showTooltip } from "@codemirror/view";
import { StateEffect, StateField } from "@codemirror/state";

// Define a state effect to show/hide the context menu tooltip
const setContextMenuEffect = StateEffect.define({
  map: (value) => value, // No transformation needed for the tooltip data
});

// StateField to manage the context menu tooltip
const contextMenuField = StateField.define({
  create: () => null, // Start with no tooltip
  update(value, tr) {
    // Check for effects to show or hide the tooltip
    for (let effect of tr.effects) {
      if (effect.is(setContextMenuEffect)) {
        return effect.value; // Set new tooltip or null to hide
      }
    }
    return value; // Keep current tooltip if no effect
  },
  provide: (f) => {
    return showTooltip.from(f); // Render tooltip from field value
  },
});

// Function to create a new Zettel via API
async function createZettelFromSelection(selectedText, boxSlug, title) {
  // Exit if no text or boxSlug is provided
  if (!selectedText || !boxSlug) {
    return;
  }

  // Construct API endpoint for the box
  const endpoint = `/api/box/${encodeURIComponent(boxSlug)}/zettels`;

  // Send POST request to create Zettel
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "", // Include CSRF token
    },
    body: JSON.stringify({
      zettel: {
        title: title || `Extracted Zettel - ${Date.now().toString(36)}`, // Use provided title or fallback
        content: selectedText, // Use selected text as content
      },
    }),
  });

  // Check if request failed
  if (!response.ok) {
    throw new Error(`Failed to create zettel: ${response.statusText}`);
  }

  // Return created Zettel data
  return await response.json();
}

// The context menu extension
export function zettelExtractor({ boxSlug }) {
  return [
    contextMenuField, // Add the tooltip field to manage menu display
    EditorView.domEventHandlers({
      // Handle right-click events in the editor
      contextmenu(event, view) {
        event.preventDefault(); // Prevent browser’s default context menu

        // Get the main selection
        const selection = view.state.selection.main;
        if (selection.empty) {
          return; // Exit if no selection
        }

        // Extract selected text
        const selectedText = view.state.doc.sliceString(selection.from, selection.to);
        if (!selectedText) {
          return; // Exit if no text
        }

        // Create the menu container
        const menuElement = document.createElement("div");
        menuElement.className = "cm-custom-context-menu"; // Use class for styling

        // Add button to create new Zettel
        const button = document.createElement("button");
        button.textContent = "↪ Create new Zettel from selection";
        button.onclick = async () => {
          try {
            // Prompt for Zettel title
            const title = window.prompt("Enter Zettel title:", `Extracted Zettel - ${Date.now().toString(36)}`);
            if (title === null) {
              return; // Exit if user cancels
            }
            const newZettel = await createZettelFromSelection(selectedText, boxSlug, title);
            if (newZettel) {
              // Optionally insert a link to the new Zettel
              view.dispatch({
                changes: {
                  from: selection.from,
                  to: selection.to,
                  insert: `[[${newZettel.title}|zettel:${newZettel.slug}]]`, // Replace selection with link
                },
                effects: setContextMenuEffect.of(null), // Hide menu
              });
            }
          } catch (error) {
            console.error("Error creating zettel:", error); // Debug: Log error
          }
        };
        menuElement.appendChild(button);

        // Define tooltip for menu display
        const tooltip = {
          pos: selection.from, // Anchor at selection start
          above: true, // Show above selection
          create: () => ({
            dom: menuElement, // Use menu element as tooltip content
          }),
        };

        view.dispatch({ effects: setContextMenuEffect.of(tooltip) }); // Show menu

        // Hide menu when clicking elsewhere
        const hideMenu = () => {
          view.dispatch({ effects: setContextMenuEffect.of(null) });
          document.removeEventListener("click", hideMenu);
        };
        document.addEventListener("click", hideMenu);
      },
    }),
  ];
}
